/** @jsxImportSource @emotion/react */
import React, { ReactElement, useMemo } from 'react';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { BreadcrumbsAliasModel } from '../../models';
import {
  breadcrumbLabels,
  RouteKeys,
  separators,
  webAppRoutes,
} from '../../constants';
import { useBreadcrumbsComponentStyles } from './BreadcrumbsComponent.styles';

interface BreadcrumbsProps {
  breadcrumbsAliases: BreadcrumbsAliasModel[];
}

interface BreadcrumbModel {
  label: string;
  url: string;
}

export function BreadcrumbsComponent({
  breadcrumbsAliases,
}: BreadcrumbsProps): ReactElement {
  const styles = useBreadcrumbsComponentStyles();

  const location = useLocation();
  const params = useParams();

  const {
    pathname,
    state: { url },
  } = location;
  const baseBreadcrumbs: BreadcrumbModel[] = [];

  const getAliasKey = (urlItem): string => {
    if (/^\s*:.+/i.test(urlItem)) {
      return urlItem.replace(/[:\s]/gi, '');
    }

    return urlItem;
  };

  const breadcrumbs = useMemo((): BreadcrumbModel[] => {
    const urlParts = url.split(separators.slash);

    const result = pathname
      .split(separators.slash)
      .reduce<BreadcrumbModel[]>((acum, item, index) => {
        if (!item) {
          return acum;
        }

        const prevBreadcrumbUrl = !acum.length
          ? webAppRoutes.home
          : acum[acum.length - 1].url;
        const key = getAliasKey(item);

        const alias = breadcrumbsAliases.find(
          (breadcrumbAlias) => breadcrumbAlias.key === key,
        );

        return [
          ...acum,
          {
            label:
              (alias && alias.label) ||
              params[key] ||
              breadcrumbLabels[key] ||
              key,
            url: `${prevBreadcrumbUrl}/${urlParts[index]}`,
          },
        ];
      }, baseBreadcrumbs);

    if (!result || !result.length) {
      return [
        {
          label: breadcrumbLabels[RouteKeys.homePage],
          url: '',
        },
      ];
    }

    return result;
  }, [params, pathname, breadcrumbsAliases, url]);

  return (
    <div css={styles}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
      >
        {breadcrumbs.map((breadcrumb, index) => (
          <div key={breadcrumb.url} className="breadcrumb-item">
            {index === breadcrumbs.length - 1 ? (
              <Typography variant="h5" color="textPrimary">
                {breadcrumb.label}
              </Typography>
            ) : (
              <Link
                color="inherit"
                className="breadcrumb-link"
                component={RouterLink}
                to={breadcrumb.url}
                variant="h5"
              >
                {breadcrumb.label}
              </Link>
            )}
          </div>
        ))}
      </Breadcrumbs>
    </div>
  );
}
